import React, { useId, useState } from "react";

import {
  STOREFRONT_BASE_URL,
  buildRecommendations,
  buildStorefrontProductUrl,
  fill,
  getCoverImage,
  getProductStatusKey,
  translateBlockingIssue,
} from "../studioEditModel";
import { Btn, Card, Chip, ConfirmDialog, Field, Icon, Notice, TextInput, Toggle, cx } from "../StudioUi";

const STATUS_CARDS = [
  { key: "published", icon: "globe", titleKey: "pbPublishedTitle", copyKey: "pbPublishedCopy", needsReady: true },
  { key: "posOnly", icon: "store", titleKey: "pbPosOnlyTitle", copyKey: "pbPosOnlyCopy", needsReady: true },
  { key: "draft", icon: "edit", titleKey: "pbDraftTitle", copyKey: "pbDraftCopy", needsReady: false },
];

const CONFIRM_COPY = {
  published: { titleKey: "pbConfirmPublishTitle", copyKey: "pbConfirmPublishCopy" },
  posOnly: { titleKey: "pbConfirmPosTitle", copyKey: "pbConfirmPosCopy" },
  draft: { titleKey: "pbConfirmDraftTitle", copyKey: "pbConfirmDraftCopy" },
  archived: { titleKey: "pbConfirmArchiveTitle", copyKey: "pbConfirmArchiveCopy", tone: "danger" },
};

/** Collapses a slug the way people expect to read it (the API still decides). */
const previewSlug = (value) =>
  String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const PublishingTab = ({ studio }) => {
  const {
    tt,
    workspace,
    coreForm,
    mediaDrafts,
    productColors,
    liveVariants,
    inventoryMode,
    dirty,
    hasUnsavedChanges,
    isSavingPublishing,
    updateCore,
    handlePublishingChange,
    selectTab,
  } = studio;
  const ids = useId();
  const [pendingStatus, setPendingStatus] = useState(null);
  const currentStatus = getProductStatusKey(workspace);
  const blockingIssues = workspace.publishing?.blockingIssues || [];
  const isBlocked = blockingIssues.length > 0;
  const recommendations = buildRecommendations({
    coreForm,
    media: mediaDrafts,
    colors: productColors,
    liveVariants,
    inventoryMode,
  });

  const savedSlug = workspace.product.slug || "";
  const slugChanged = (coreForm.slug || "") !== savedSlug;
  const effectiveSlug = previewSlug(coreForm.slug) || previewSlug(coreForm.title);
  const liveUrl = buildStorefrontProductUrl(savedSlug);
  const cover = getCoverImage(mediaDrafts);
  const description = String(coreForm.description || "").trim();

  const confirmCopy = pendingStatus ? CONFIRM_COPY[pendingStatus] : null;
  const confirmText =
    pendingStatus === "posOnly" && currentStatus === "published" ? tt("pbConfirmPosCopyFromLive") : confirmCopy && tt(confirmCopy.copyKey);

  const confirm = async () => {
    const target = pendingStatus;
    const ok = await handlePublishingChange(target);
    if (ok) setPendingStatus(null);
  };

  return (
    <div className="pse-layout">
      <div className="pse-layout__main">
        <Card title={tt("pbStatusTitle")} subtitle={tt("pbStatusCopy")}>
          {currentStatus === "archived" ? (
            <Notice tone="danger" title={tt("pbArchivedTitle")}>
              {tt("pbArchivedCopy")}
            </Notice>
          ) : null}
          <div className="pse-status-cards">
            {STATUS_CARDS.map((card) => {
              const isCurrent = currentStatus === card.key;
              const disabled = isCurrent || isSavingPublishing || (card.needsReady && isBlocked);
              return (
                <div key={card.key} className={cx("pse-status-card", isCurrent && "is-current", `is-${card.key}`)}>
                  <div className="pse-status-card__head">
                    <span className="pse-status-card__icon">
                      <Icon name={isCurrent ? "checkCircle" : card.icon} size={22} />
                    </span>
                    <strong>{tt(card.titleKey)}</strong>
                  </div>
                  <p>{tt(card.copyKey)}</p>
                  {isCurrent ? (
                    <Chip tone="success">{tt("pbIsCurrent")}</Chip>
                  ) : (
                    <Btn
                      variant={card.key === "published" ? "primary" : "secondary"}
                      size="sm"
                      disabled={disabled}
                      title={card.needsReady && isBlocked ? tt("pbBlockedBy") : undefined}
                      onClick={() => setPendingStatus(card.key)}
                    >
                      {tt("pbChoose")}
                    </Btn>
                  )}
                  {!isCurrent && card.needsReady && isBlocked ? (
                    <small className="pse-status-card__blocked">
                      <Icon name="lock" size={13} /> {tt("pbBlockedBy")}
                    </small>
                  ) : null}
                </div>
              );
            })}
          </div>

          <Toggle
            checked={coreForm.metaCatalogEnabled}
            onChange={(checked) => updateCore("metaCatalogEnabled", checked)}
            label={
              <span className="pse-inline">
                <Icon name="meta" size={18} /> {tt("pbMetaTitle")}
                {dirty.core && coreForm.metaCatalogEnabled !== Boolean(workspace.product.metaCatalogEnabled) ? (
                  <Chip tone="warning">{tt("unsavedChip")}</Chip>
                ) : null}
              </span>
            }
            description={`${tt("pbMetaCopy")} ${tt("pbMetaSaveHint")}`}
          />
        </Card>

        <div className="pse-grid pse-grid--2 pse-grid--align-start">
          <Card title={tt("pbSeoTitle")} subtitle={tt("pbSeoCopy")}>
            <Field
              label={tt("pbSlug")}
              htmlFor={`${ids}-slug`}
              hint={tt("pbSlugNormalized")}
              counter={`${(coreForm.slug || "").length}/200`}
            >
              <TextInput
                id={`${ids}-slug`}
                maxLength={200}
                value={coreForm.slug}
                placeholder={tt("pbSlugPlaceholder")}
                onChange={(value) => updateCore("slug", value)}
              />
            </Field>
            <p className="pse-url-preview">
              <Icon name="globe" size={15} />
              <span>
                {STOREFRONT_BASE_URL}/shop/<strong>{effectiveSlug || "…"}</strong>
              </span>
              {currentStatus === "published" && liveUrl && !slugChanged ? (
                <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="pse-icon-btn pse-icon-btn--sm" aria-label={tt("viewInStore")}>
                  <Icon name="external" size={15} />
                </a>
              ) : null}
            </p>
            {slugChanged && savedSlug ? <Notice tone="warning">{tt("pbSlugWarning")}</Notice> : null}
          </Card>

          <Card title={tt("pbGoogleTitle")} subtitle={tt("pbGoogleCopy")}>
            <div className="pse-serp">
              <div className="pse-serp__text">
                <span className="pse-serp__crumb">
                  {STOREFRONT_BASE_URL.replace(/^https?:\/\//, "")} › shop › {effectiveSlug || "…"}
                </span>
                <span className="pse-serp__title">{coreForm.title || "—"}</span>
                <span className="pse-serp__description">
                  {description ? description.slice(0, 160) + (description.length > 160 ? "…" : "") : tt("pbGoogleNoDescription")}
                </span>
              </div>
              {cover?.sourceUrl ? <img className="pse-serp__image" src={cover.sourceUrl} alt="" /> : null}
            </div>
          </Card>
        </div>
      </div>

      <aside className="pse-layout__side">
        <Card title={tt("pbChecklist")} subtitle={tt("pbChecklistCopy")}>
          <h4 className="pse-subheading">{tt("pbBlockersTitle")}</h4>
          {isBlocked ? (
            <ul className="pse-checklist">
              {blockingIssues.map((issue) => (
                <li key={issue} className="pse-checklist__item is-blocked">
                  <Icon name="x" size={18} />
                  <span>{translateBlockingIssue(issue, tt)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="pse-inline pse-positive">
              <Icon name="checkCircle" size={18} /> {tt("pbBlockersNone")}
            </p>
          )}

          {recommendations.length ? (
            <>
              <h4 className="pse-subheading">{tt("pbRecommendations")}</h4>
              <ul className="pse-checklist">
                {recommendations.map((recommendation) => (
                  <li key={recommendation.key} className="pse-checklist__item is-warning">
                    <Icon name="alert" size={18} />
                    <span>{fill(tt(recommendation.key), recommendation.values || {})}</span>
                    <button type="button" className="pse-link" onClick={() => selectTab(recommendation.tab)}>
                      {tt("edit")}
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </Card>

        {currentStatus !== "archived" ? (
          <Card title={tt("pbDanger")} subtitle={tt("pbDangerCopy")} className="pse-card--danger">
            <Btn variant="danger-ghost" block icon="archive" onClick={() => setPendingStatus("archived")} disabled={isSavingPublishing}>
              {tt("pbArchive")}
            </Btn>
          </Card>
        ) : null}
      </aside>

      <ConfirmDialog
        isOpen={Boolean(pendingStatus)}
        title={confirmCopy ? tt(confirmCopy.titleKey) : ""}
        confirmLabel={
          isSavingPublishing ? tt("saving") : pendingStatus === "archived" ? tt("pbConfirmArchiveAction") : tt("pbConfirmAction")
        }
        cancelLabel={tt("cancel")}
        tone={confirmCopy?.tone || "primary"}
        isBusy={isSavingPublishing}
        onCancel={() => setPendingStatus(null)}
        onConfirm={confirm}
      >
        <p>{confirmText}</p>
        {hasUnsavedChanges ? <p className="pse-dialog__warning">{tt("pbUnsavedWarning")}</p> : null}
      </ConfirmDialog>
    </div>
  );
};

export default PublishingTab;
